import React, { useState } from 'react';
import './Withdraw.css';

const Withdraw = () => {
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    network: '',
    walletAddress: '',
    phoneNumber: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    currency: 'BTC'
  });
  const [errors, setErrors] = useState({});
  const [transactionId, setTransactionId] = useState('');
  const [securityStatus, setSecurityStatus] = useState({
    phoneVerified: false,
    twoFactorEnabled: false
  });
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false
  });
  const [maxAmount] = useState(0.00000000); // This would come from API
  const [minWithdrawal] = useState(0.00100000);
  const [withdrawalFee] = useState(0.00030000);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      window.history.back();
    }
  };

  const selectMethod = (method) => {
    setSelectedMethod(method);
    setFormData({
      amount: '',
      network: '',
      walletAddress: '',
      phoneNumber: '',
      bankName: '',
      accountNumber: '',
      accountName: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const updateFeeSummary = () => {
    // Fee calculation logic
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedMethod === 'crypto') {
      const amount = parseFloat(formData.amount) || 0;
      if (!formData.amount || amount < minWithdrawal) {
        newErrors.amount = `Amount must be at least ${minWithdrawal} BTC`;
      }
      if (!formData.walletAddress) {
        newErrors.walletAddress = 'Please enter wallet address';
      }
      if (!checkboxes.checkbox1 || !checkboxes.checkbox2) {
        newErrors.checkboxes = 'Please accept all terms and conditions';
      }
    } else {
      if (!formData.amount || parseFloat(formData.amount) < 10 || parseFloat(formData.amount) > 10000) {
        newErrors.amount = 'Amount must be between $10 and $10,000';
      }
    }

    if (selectedMethod === 'mpesa') {
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = 'Please enter phone number';
      }
    } else if (selectedMethod === 'bank') {
      if (!formData.bankName) {
        newErrors.bankName = 'Please enter bank name';
      }
      if (!formData.accountNumber) {
        newErrors.accountNumber = 'Please enter account number';
      }
      if (!formData.accountName) {
        newErrors.accountName = 'Please enter account holder name';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitWithdrawal = () => {
    // Get user data from localStorage
    const userData = localStorage.getItem('userData');
    if (!userData) return null;

    try {
      const user = JSON.parse(userData);
      const withdrawalId = Date.now();
      const withdrawal = {
        id: withdrawalId,
        userId: user.email,
        userEmail: user.email,
        userName: user.name || user.email.split('@')[0],
        amount: parseFloat(formData.amount) || 0,
        currency: formData.currency || 'USD',
        method: selectedMethod,
        network: formData.network || '',
        walletAddress: formData.walletAddress || '',
        phoneNumber: formData.phoneNumber || '',
        bankName: formData.bankName || '',
        accountNumber: formData.accountNumber || '',
        accountName: formData.accountName || '',
        status: 'pending',
        submittedAt: new Date().toISOString(),
        fee: selectedMethod === 'crypto' && formData.currency === 'BTC' ? withdrawalFee : (parseFloat(formData.amount) || 0) * 0.01,
      };

      // Get existing withdrawals from localStorage
      const existingWithdrawals = localStorage.getItem('adminWithdrawals');
      let withdrawals = [];
      if (existingWithdrawals) {
        try {
          withdrawals = JSON.parse(existingWithdrawals);
        } catch (e) {
          console.error('Error parsing withdrawals:', e);
        }
      }

      // Add new withdrawal
      withdrawals.unshift(withdrawal);
      localStorage.setItem('adminWithdrawals', JSON.stringify(withdrawals));

      return withdrawalId;
    } catch (e) {
      console.error('Error submitting withdrawal:', e);
      return null;
    }
  };

  const handleAction = () => {
    if (step === 1) {
      if (selectedMethod) {
        setStep(2);
      }
    } else if (step === 2) {
      if (selectedMethod === 'crypto') {
        // For crypto, validate and submit directly
        if (validateForm()) {
          const id = submitWithdrawal();
          if (id) {
            setTransactionId('TX-' + id);
            setStep(4);
          }
        }
      } else {
        // For other methods, go to confirmation
        if (validateForm()) {
          setStep(3);
        }
      }
    } else if (step === 3) {
      // Submit withdrawal
      const id = submitWithdrawal();
      if (id) {
        setTransactionId('TX-' + id);
        setStep(4);
      }
    }
  };

  const copyTransactionId = () => {
    navigator.clipboard.writeText(transactionId);
    alert('Transaction ID copied!');
  };

  const calculateFee = () => {
    if (selectedMethod === 'crypto' && formData.currency === 'BTC') {
      const amount = parseFloat(formData.amount) || 0;
      const fee = withdrawalFee; // Fixed fee for BTC
      const total = amount - fee;
      return { amount, fee, total: Math.max(0, total) };
    } else {
      const amount = parseFloat(formData.amount) || 0;
      const feePercent = 0.01; // 1%
      const fee = amount * feePercent;
      return { amount, fee, total: amount - fee };
    }
  };

  const { amount, fee, total } = calculateFee();
  const kesEquivalent = (amount * 130).toFixed(2); // Approximate conversion

  const handleCheckboxChange = (name) => {
    setCheckboxes(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleMaxAmount = () => {
    setFormData(prev => ({
      ...prev,
      amount: maxAmount.toString()
    }));
  };

  const handleOpenSettings = () => {
    window.history.pushState({ page: 'general-settings' }, '', '/general-settings/settings');
    window.location.reload();
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'crypto':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042.348-1.97M7.48 20.364l3.126-17.727"></path>
          </svg>
        );
      case 'mpesa':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><path d="M12 18h.01"></path>
          </svg>
        );
      case 'bank':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18"></path><path d="M3 10h18"></path><path d="M5 6l7-3 7 3"></path><path d="M4 10v11"></path><path d="M20 10v11"></path><path d="M8 14v3"></path><path d="M12 14v3"></path><path d="M16 14v3"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  const getMethodName = (method) => {
    switch (method) {
      case 'crypto': return 'Cryptocurrency';
      case 'mpesa': return 'M-Pesa';
      case 'bank': return 'Bank Transfer';
      default: return '';
    }
  };

  const getProcessingTime = (method) => {
    switch (method) {
      case 'crypto': return 'Processing time: 1-24 hours';
      case 'mpesa': return 'Processing time: Instant';
      case 'bank': return 'Processing time: 2-5 business days';
      default: return '';
    }
  };

  return (
    <div className="card withdraw-card">
      {/* Header */}
      <div className="headerz">
        <div className="headerz-left">
          <button className="back-btn" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"></path>
            </svg>
          </button>
          <div style={{ marginTop: '0.5rem' }}>
            <h1>Withdraw Funds</h1>
            <p>Withdraw your earnings securely</p>
          </div>
        </div>
        <button className="help-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <path d="M12 17h.01"></path>
          </svg>
        </button>
      </div>

      {/* Steps */}
      <div className="steps-container">
        <div className="steps">
          <div className="step">
            <div className={`step-number ${step >= 1 ? 'active' : ''}`}>1</div>
            <span className="step-label">Method</span>
          </div>
          <div className={`step-line ${step >= 2 ? 'active' : ''}`}></div>
          <div className="step">
            <div className={`step-number ${step >= 2 ? 'active' : ''}`}>2</div>
            <span className="step-label">Details</span>
          </div>
          <div className={`step-line ${step >= 3 ? 'active' : ''}`}></div>
          <div className="step">
            <div className={`step-number ${step >= 3 ? 'active' : ''}`}>3</div>
            <span className="step-label">Confirm</span>
          </div>
        </div>
        <div className="badges">
          <div className="badge">
            <svg className="success" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <span>Secure Withdrawals</span>
          </div>
          <div className="badge">
            <svg className="primary" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
            <span>Fast Processing</span>
          </div>
        </div>
      </div>

      {/* Step 1: Method Selection */}
      {step === 1 && (
        <div id="methodSection">
          <div className="methods">
            <button
              className={`method-card ${selectedMethod === 'crypto' ? 'selected' : ''}`}
              onClick={() => selectMethod('crypto')}
            >
              <span className="method-badge popular">Popular</span>
              <div className="method-content">
                <div className="method-icon">
                  {getMethodIcon('crypto')}
                </div>
                <div className="method-info">
                  <h3>Cryptocurrency</h3>
                  <p>Fast, secure withdrawals to BTC, ETH, USDT & more. Processed within 24 hours.</p>
                </div>
                <div className={`method-check ${selectedMethod === 'crypto' ? 'visible' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>
            </button>

            <button
              className={`method-card ${selectedMethod === 'mpesa' ? 'selected' : ''}`}
              onClick={() => selectMethod('mpesa')}
            >
              <span className="method-badge instant">Instant</span>
              <div className="method-content">
                <div className="method-icon">
                  {getMethodIcon('mpesa')}
                </div>
                <div className="method-info">
                  <h3>M-Pesa</h3>
                  <p>Instant mobile money withdrawals directly to your phone. Available 24/7.</p>
                </div>
                <div className={`method-check ${selectedMethod === 'mpesa' ? 'visible' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>
            </button>

            <button
              className={`method-card ${selectedMethod === 'bank' ? 'selected' : ''}`}
              onClick={() => selectMethod('bank')}
            >
              <div className="method-content">
                <div className="method-icon">
                  {getMethodIcon('bank')}
                </div>
                <div className="method-info">
                  <h3>Bank Transfer</h3>
                  <p>Direct bank wire for large amounts. Secure transfers processed globally.</p>
                </div>
                <div className={`method-check ${selectedMethod === 'bank' ? 'visible' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>
            </button>
          </div>

          <div className="benefits">
            <h3>Why withdraw with us?</h3>
            <ul>
              <li>Fast processing within 24 hours</li>
              <li>Low withdrawal fees</li>
              <li>24/7 customer support</li>
              <li>Bank-grade security encryption</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <form id="withdrawalForm" onSubmit={(e) => { e.preventDefault(); handleAction(); }}>
          <input type="hidden" id="selectedMethod" name="method" value={selectedMethod} />

          <div className="form-section" id="detailsSection">
            {/* Withdrawal Amount */}
            <div className="form-group">
              <label htmlFor="amount">Withdrawal Amount (USD)</label>
              <div className="input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  className="has-prefix"
                  placeholder="0.00"
                  min="10"
                  max="10000"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onInput={updateFeeSummary}
                  required
                />
              </div>
              {errors.amount && (
                <div className="error-message" id="amountError">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 8v4M12 16h.01"></path>
                  </svg>
                  <span>{errors.amount}</span>
                </div>
              )}
              <p className="input-hint">Min: $10 • Max: $10,000</p>
              <p className="input-hint" id="kesEquivalent" style={{ fontWeight: 600, color: '#0f9d58' }}>
                ≈ KES {kesEquivalent}
              </p>
            </div>

            {/* Security Verification */}
            {selectedMethod === 'crypto' && (
              <div className="withdrawal-form__verification">
                <div className="withdrawal-form__verification-title">Security requirements</div>
                <ul className="withdrawal-form__verification-list">
                  {!securityStatus.phoneVerified && (
                    <li className="withdrawal-form__verification-item bad">
                      <span className="withdrawal-form__verification-dot"></span>
                      <span>Phone not verified</span>
                    </li>
                  )}
                  {!securityStatus.twoFactorEnabled && (
                    <li className="withdrawal-form__verification-item bad">
                      <span className="withdrawal-form__verification-dot"></span>
                      <span>Two-factor authentication is not enabled</span>
                    </li>
                  )}
                </ul>
                <a
                  href="/general-settings/settings"
                  className="withdrawal-form__verification-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenSettings();
                  }}
                >
                  Open settings
                </a>
              </div>
            )}

            {/* Crypto Fields */}
            {selectedMethod === 'crypto' && (
              <div id="cryptoFields" className="withdrawal-form">
                <div className="withdrawal-form__section">
                  <label className="withdrawal-form__label">Choose the currency you want to withdraw:</label>
                  <div className="withdrawal-form__currency">
                    <div className="withdrawal-form__currency-badge">
                      <img
                        src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%3e%3cg%20clip-path='url(%23clip0_272_22442)'%3e%3cpath%20d='M24%2012C24%2014.3734%2023.2962%2016.6935%2021.9776%2018.6668C20.6591%2020.6402%2018.7849%2022.1783%2016.5922%2023.0866C14.3995%2023.9948%2011.9867%2024.2324%209.65892%2023.7694C7.33115%2023.3064%205.19295%2022.1635%203.51472%2020.4853C1.83649%2018.8071%200.693605%2016.6689%200.230582%2014.3411C-0.232441%2012.0133%200.00519936%209.60051%200.913451%207.4078C1.8217%205.21509%203.35977%203.34094%205.33316%202.02236C7.30655%200.703788%209.62663%200%2012%200C15.1826%200%2018.2348%201.26428%2020.4853%203.51472C22.7357%205.76516%2024%208.8174%2024%2012Z'%20fill='%23F7931A'/%3e%3cpath%20fill-rule='evenodd'%20clip-rule='evenodd'%20d='M6.713%206.60268L9.568%206.60229L9.56874%204.22359L10.9975%204.22813L10.9954%206.50894L12.16%206.50973L12.157%204.20545L13.6092%204.20484L13.6048%206.52353C13.6048%206.52353%2015.9489%206.42059%2016.9517%208.08089C17.9545%209.74119%2016.5429%2011.1325%2016.0383%2011.3061C16.0383%2011.3061%2018.262%2011.8498%2018.1565%2014.1199C18.051%2016.39%2016.107%2017.3119%2013.8279%2017.4087L13.8449%2019.8075L12.3927%2019.8081L12.3966%2017.441L11.2432%2017.436L11.2436%2019.8189L9.80222%2019.8178L9.80571%2017.4451L6.87362%2017.4402L7.15279%2015.7255C7.15279%2015.7255%207.9806%2015.7296%208.29112%2015.7209C8.60164%2015.7123%208.70789%2015.3585%208.70876%2015.033C8.70964%2014.7075%208.60013%209.34629%208.60613%208.90083C8.61213%208.45537%208.47804%208.14458%207.95283%208.14865C7.42762%208.15272%206.70619%208.13257%206.70619%208.13257L6.713%206.60268ZM11.1848%2012.5434L11.2329%2015.6736C11.2329%2015.6736%2015.2442%2016.0139%2015.2143%2014.0308C15.1844%2012.0477%2011.1848%2012.5434%2011.1848%2012.5434ZM11.1514%2011.0615L11.1553%208.18052C11.1553%208.18052%2014.4372%207.90197%2014.4522%209.50056C14.4672%2011.0992%2012.4674%2011.0206%2011.1514%2011.0615Z'%20fill='white'/%3e%3c/g%3e%3cdefs%3e%3cclipPath%20id='clip0_272_22442'%3e%3crect%20width='24'%20height='24'%20fill='white'/%3e%3c/clipPath%3e%3c/defs%3e%3c/svg%3e"
                        alt="BTC"
                        width="24"
                        height="24"
                      />
                      <span className="withdrawal-form__currency-name">BTC</span>
                    </div>
                  </div>
                </div>

                <div className="withdrawal-form__section">
                  <label className="withdrawal-form__label">Enter the wallet address:</label>
                  <div className="withdrawal-form__field">
                    <input
                      type="text"
                      className="withdrawal-form__input"
                      placeholder="Wallet address"
                      name="walletAddress"
                      value={formData.walletAddress}
                      onChange={handleInputChange}
                    />
                    <button type="button" className="withdrawal-form__qr-button">
                      <img
                        src="https://cp.ecos.am/_nuxt/qr.C6-ZlJdI.svg"
                        alt="QR code scanner"
                        width="18"
                        height="18"
                      />
                    </button>
                  </div>
                  {errors.walletAddress && (
                    <div className="error-message">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.walletAddress}</span>
                    </div>
                  )}
                </div>

                <div className="withdrawal-form__section">
                  <label className="withdrawal-form__label">Enter the amount you want to withdraw:</label>
                  <div className="withdrawal-form__field">
                    <input
                      type="text"
                      className="withdrawal-form__input"
                      placeholder="0.00000000"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                    />
                    <div className="withdrawal-form__currency-indicator">
                      <img
                        src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%20fill='none'%3e%3cg%20clip-path='url(%23clip0_272_22442)'%3e%3cpath%20d='M24%2012C24%2014.3734%2023.2962%2016.6935%2021.9776%2018.6668C20.6591%2020.6402%2018.7849%2022.1783%2016.5922%2023.0866C14.3995%2023.9948%2011.9867%2024.2324%209.65892%2023.7694C7.33115%2023.3064%205.19295%2022.1635%203.51472%2020.4853C1.83649%2018.8071%200.693605%2016.6689%200.230582%2014.3411C-0.232441%2012.0133%200.00519936%209.60051%200.913451%207.4078C1.8217%205.21509%203.35977%203.34094%205.33316%202.02236C7.30655%200.703788%209.62663%200%2012%200C15.1826%200%2018.2348%201.26428%2020.4853%203.51472C22.7357%205.76516%2024%208.8174%2024%2012Z'%20fill='%23F7931A'/%3e%3cpath%20fill-rule='evenodd'%20clip-rule='evenodd'%20d='M6.713%206.60268L9.568%206.60229L9.56874%204.22359L10.9975%204.22813L10.9954%206.50894L12.16%206.50973L12.157%204.20545L13.6092%204.20484L13.6048%206.52353C13.6048%206.52353%2015.9489%206.42059%2016.9517%208.08089C17.9545%209.74119%2016.5429%2011.1325%2016.0383%2011.3061C16.0383%2011.3061%2018.262%2011.8498%2018.1565%2014.1199C18.051%2016.39%2016.107%2017.3119%2013.8279%2017.4087L13.8449%2019.8075L12.3927%2019.8081L12.3966%2017.441L11.2432%2017.436L11.2436%2019.8189L9.80222%2019.8178L9.80571%2017.4451L6.87362%2017.4402L7.15279%2015.7255C7.15279%2015.7255%207.9806%2015.7296%208.29112%2015.7209C8.60164%2015.7123%208.70789%2015.3585%208.70876%2015.033C8.70964%2014.7075%208.60013%209.34629%208.60613%208.90083C8.61213%208.45537%208.47804%208.14458%207.95283%208.14865C7.42762%208.15272%206.70619%208.13257%206.70619%208.13257L6.713%206.60268ZM11.1848%2012.5434L11.2329%2015.6736C11.2329%2015.6736%2015.2442%2016.0139%2015.2143%2014.0308C15.1844%2012.0477%2011.1848%2012.5434%2011.1848%2012.5434ZM11.1514%2011.0615L11.1553%208.18052C11.1553%208.18052%2014.4372%207.90197%2014.4522%209.50056C14.4672%2011.0992%2012.4674%2011.0206%2011.1514%2011.0615Z'%20fill='white'/%3e%3c/g%3e%3cdefs%3e%3cclipPath%20id='clip0_272_22442'%3e%3crect%20width='24'%20height='24'%20fill='white'/%3e%3c/clipPath%3e%3c/defs%3e%3c/svg%3e"
                        alt="BTC"
                        width="24"
                        height="24"
                      />
                      <span className="withdrawal-form__currency-name">BTC</span>
                    </div>
                  </div>
                  <div className="withdrawal-form__amount-info">
                    <button type="button" className="withdrawal-form__max-button" onClick={handleMaxAmount}>
                      Max {maxAmount.toFixed(8)} BTC
                    </button>
                    <div className="withdrawal-form__limits">
                      <span className="withdrawal-form__limit-item">Min withdrawal: {minWithdrawal.toFixed(8)} BTC</span>
                      <span className="withdrawal-form__limit-item">Withdrawal fee: {withdrawalFee.toFixed(8)} BTC</span>
                    </div>
                  </div>
                </div>

                <div className="withdrawal-form__section">
                  <p className="withdrawal-form__receive">
                    You will receive: <strong className="withdrawal-form__receive-amount">{total.toFixed(8)} BTC</strong>
                  </p>
                  <span className="withdrawal-form__receive-border"></span>
                </div>

                <div className="withdrawal-form__section">
                  <label className="ui-checkbox withdrawal-form__checkbox">
                    <input
                      type="checkbox"
                      className="ui-checkbox__input"
                      name="checkbox1"
                      checked={checkboxes.checkbox1}
                      onChange={() => handleCheckboxChange('checkbox1')}
                    />
                    <span className="ui-checkbox__visual"></span>
                    <span className="ui-checkbox__wrap">
                      <span className="ui-checkbox__text">
                        I understand and accept that additional fees for this transaction might be applied by a bank.
                      </span>
                    </span>
                  </label>
                  <label className="ui-checkbox withdrawal-form__checkbox">
                    <input
                      type="checkbox"
                      className="ui-checkbox__input"
                      name="checkbox2"
                      checked={checkboxes.checkbox2}
                      onChange={() => handleCheckboxChange('checkbox2')}
                    />
                    <span className="ui-checkbox__visual"></span>
                    <span className="ui-checkbox__wrap">
                      <span className="ui-checkbox__text">
                        All withdrawals are reviewed by our team for security and identification within 24 hours (except weekends and holidays). Confirmation will be requested via the email in your profile. Please ensure that your wallet address matches the network (e.g., ETH for ERC-20). If you have any questions, feel free to contact support.
                      </span>
                    </span>
                  </label>
                  {errors.checkboxes && (
                    <div className="error-message">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.checkboxes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* M-Pesa Fields */}
            {selectedMethod === 'mpesa' && (
              <div id="mpesaFields">
                <div className="form-group">
                  <label htmlFor="phoneNumber">M-Pesa Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="+254 7XX XXX XXX"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.phoneNumber && (
                    <div className="error-message" id="phoneError">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.phoneNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bank Fields */}
            {selectedMethod === 'bank' && (
              <div id="bankFields">
                <div className="form-group">
                  <label htmlFor="bankName">Bank Name</label>
                  <input
                    type="text"
                    id="bankName"
                    name="bankName"
                    placeholder="Enter bank name"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.bankName && (
                    <div className="error-message" id="bankNameError">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.bankName}</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="accountNumber">Account Number</label>
                  <input
                    type="text"
                    id="accountNumber"
                    name="accountNumber"
                    placeholder="Enter account number"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.accountNumber && (
                    <div className="error-message" id="accountNumberError">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.accountNumber}</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="accountName">Account Holder Name</label>
                  <input
                    type="text"
                    id="accountName"
                    name="accountName"
                    placeholder="Enter account holder name"
                    value={formData.accountName}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.accountName && (
                    <div className="error-message" id="accountNameError">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4M12 16h.01"></path>
                      </svg>
                      <span>{errors.accountName}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fee Summary */}
            <div className="fee-summary" id="feeSummary">
              <h4>Fee Summary</h4>
              <div className="fee-row">
                <span className="label">Amount</span>
                <span className="value" id="feeAmount">${amount.toFixed(2)}</span>
              </div>
              <div className="fee-row">
                <span className="label" id="feePercentLabel">Fee (1%)</span>
                <span className="value negative" id="feeValue">-${fee.toFixed(2)}</span>
              </div>
              <div className="fee-row total">
                <span className="label">You'll Receive</span>
                <span className="value" id="feeTotal">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </form>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <div className="confirm-section" id="confirmSection">
          <div className="summary-card">
            <h4>Withdrawal Summary</h4>
            <div className="summary-method">
              <div className="summary-method-icon">
                {getMethodIcon(selectedMethod)}
              </div>
              <div className="summary-method-info">
                <p id="summaryMethodName">{getMethodName(selectedMethod)}</p>
                <span id="summaryProcessing">{getProcessingTime(selectedMethod)}</span>
              </div>
            </div>
            <div className="summary-destination">
              <p className="label">Destination</p>
              <p className="value" id="summaryDestination">
                {selectedMethod === 'crypto' && formData.walletAddress}
                {selectedMethod === 'mpesa' && formData.phoneNumber}
                {selectedMethod === 'bank' && `${formData.bankName} - ${formData.accountNumber}`}
              </p>
              <p className="extra" id="summaryExtra">
                {selectedMethod === 'crypto' && `Network: ${formData.network}`}
                {selectedMethod === 'bank' && `Account Name: ${formData.accountName}`}
              </p>
            </div>
            <div style={{ paddingTop: '1rem' }}>
              <div className="fee-row">
                <span className="label">Withdrawal Amount</span>
                <span className="value" id="confirmAmount" style={{ fontWeight: 600 }}>${amount.toFixed(2)}</span>
              </div>
              <div className="fee-row">
                <span className="label" id="confirmFeeLabel">Fee (1%)</span>
                <span className="value negative" id="confirmFee">-${fee.toFixed(2)}</span>
              </div>
              <div className="fee-row total">
                <span className="label">You'll Receive</span>
                <span className="value" id="confirmTotal" style={{ fontSize: '1.125rem' }}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="warning-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 8v4M12 16h.01"></path>
            </svg>
            <div className="text">
              <h5>Please verify your details</h5>
              <p>Ensure all information is correct. Withdrawals to incorrect addresses cannot be reversed.</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Section */}
      {step === 4 && (
        <div className="success-section" id="successSection">
          <div className="success-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2>Withdrawal Submitted!</h2>
          <p>Your withdrawal request has been submitted and is being processed.</p>
          <div className="transaction-info">
            <div className="transaction-id">
              <span>Transaction ID:</span>
              <strong id="transactionId">{transactionId}</strong>
              <button className="copy-btn" onClick={copyTransactionId}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
            <p className="processing-time" id="successProcessing">{getProcessingTime(selectedMethod)}</p>
          </div>
          <div className="success-summary">
            <div className="fee-row">
              <span className="label">Amount</span>
              <span className="value" id="successAmount">${amount.toFixed(2)}</span>
            </div>
            <div className="fee-row">
              <span className="label" id="successFeeLabel">Fee (1%)</span>
              <span className="value negative" id="successFee">-${fee.toFixed(2)}</span>
            </div>
            <div className="fee-row total">
              <span className="label">You'll Receive</span>
              <span className="value" id="successTotal">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      {step < 4 && (
        <>
          {step === 2 && selectedMethod === 'crypto' && (
            <button
              className="orange withdrawal-form__button"
              type="button"
              disabled={!formData.amount || !formData.walletAddress || !checkboxes.checkbox1 || !checkboxes.checkbox2}
              onClick={handleAction}
            >
              Withdraw now
            </button>
          )}
          {!(step === 2 && selectedMethod === 'crypto') && (
            <button
              className={`action-btn primary ${(step === 1 && !selectedMethod) || (step === 2 && !formData.amount) ? 'disabled' : ''}`}
              id="actionBtn"
              disabled={(step === 1 && !selectedMethod) || (step === 2 && !formData.amount)}
              onClick={handleAction}
            >
              {step === 1 && 'Continue to Details'}
              {step === 2 && 'Continue to Confirm'}
              {step === 3 && 'Confirm Withdrawal'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Withdraw;

